import { useState, type FormEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDateTime } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { KeyRound, Mail, Send, UserX, UserPlus } from "lucide-react";

interface PortalUser {
    id: string;
    customer_id: string;
    email: string;
    display_name: string | null;
    is_active: boolean;
    last_login_at: string | null;
    created_at: string;
}

export function CustomerPortalAccessPanel({ customerId }: { customerId: string }) {
    const { isOffice } = useAuth();
    const qc = useQueryClient();
    const [email, setEmail] = useState("");
    const [displayName, setDisplayName] = useState("");

    const queryKey = ["portal-users", customerId];
    const { data, isLoading } = useQuery({
        queryKey,
        queryFn: () => api.get<{ portalUsers: PortalUser[] }>(`/api/customers/${customerId}/portal-users`),
        enabled: isOffice,
    });

    const invite = useMutation({
        mutationFn: (body: { email: string; displayName?: string }) =>
            api.post(`/api/customers/${customerId}/portal-users`, body),
        onSuccess: () => {
            toast.success("Portal invite sent");
            setEmail("");
            setDisplayName("");
            qc.invalidateQueries({ queryKey });
        },
        onError: (err: unknown) => {
            const code = (err as { body?: { error?: string } })?.body?.error;
            toast.error(
                code === "EMAIL_IN_USE" ? "That email already has a portal account." : "Failed to send portal invite.",
            );
        },
    });

    const resend = useMutation({
        mutationFn: (id: string) => api.post(`/api/portal-users/${id}/send-login-link`, {}),
        onSuccess: () => toast.success("Login link sent"),
        onError: () => toast.error("Failed to send login link."),
    });

    const deactivate = useMutation({
        mutationFn: (id: string) => api.post(`/api/portal-users/${id}/deactivate`, {}),
        onSuccess: () => {
            toast.success("Portal access revoked");
            qc.invalidateQueries({ queryKey });
        },
        onError: () => toast.error("Failed to revoke access."),
    });

    if (!isOffice) return null;

    const handleInvite = (e: FormEvent) => {
        e.preventDefault();
        if (!email.trim()) return;
        invite.mutate({ email: email.trim(), displayName: displayName.trim() || undefined });
    };

    const users = data?.portalUsers ?? [];

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                    <KeyRound className="h-4 w-4" /> Customer Portal Access
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {isLoading ? (
                    <p className="text-sm text-foreground/80">Loading…</p>
                ) : users.length === 0 ? (
                    <p className="text-sm text-foreground/80">No portal users yet.</p>
                ) : (
                    <div className="space-y-2">
                        {users.map((u) => (
                            <div key={u.id} className="flex items-center justify-between gap-3 rounded-lg border p-2.5">
                                <div className="min-w-0">
                                    <p className="text-sm font-medium truncate flex items-center gap-1.5">
                                        <Mail className="h-3 w-3 shrink-0 text-foreground/60" />
                                        {u.email}
                                    </p>
                                    <p className="text-xs text-foreground/70 truncate">
                                        {u.display_name ? `${u.display_name} · ` : ""}
                                        {u.last_login_at
                                            ? `Last login ${formatDateTime(u.last_login_at)}`
                                            : "Never signed in"}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <Badge className={u.is_active ? "bg-green-100 text-green-800" : "bg-muted"}>
                                        {u.is_active ? "Active" : "Inactive"}
                                    </Badge>
                                    {u.is_active && (
                                        <>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => resend.mutate(u.id)}
                                                disabled={resend.isPending}
                                                title="Resend login link"
                                            >
                                                <Send className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => deactivate.mutate(u.id)}
                                                disabled={deactivate.isPending}
                                                title="Revoke access"
                                            >
                                                <UserX className="h-3.5 w-3.5 text-destructive" />
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <form onSubmit={handleInvite} className="space-y-2 border-t pt-3">
                    <Input
                        type="email"
                        placeholder="customer@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                    <Input
                        type="text"
                        placeholder="Display name (optional)"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                    />
                    <Button type="submit" size="sm" className="w-full" disabled={invite.isPending}>
                        <UserPlus className="h-4 w-4 mr-1" />
                        {invite.isPending ? "Sending…" : "Invite to Portal"}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
}
